import React from "react";
import { toYmd, ymdToDate } from '../utils/dateOnly';
import { useRef, useEffect } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { ProductAutoCompleteCell } from "./ProductSelector";

const PrescriptionDialog = ({
    visible,
    onHide,
    prescription,
    handleItemChange,
    handleSearchCustomer,
    handleDurationChange,
    handleSubmit,
    handleRevise,
    handleConvertClick,
    handleCustomerDialogVisible,
    setPrescription,
    removeItem,
    isReadOnly,
    isRevising,
    frequency,
    dialogName,
    filteredCustomers,
    header,
    modulehandler,
    cancelhandler,
    prodInputRefs,
    addNewItem,
    isRefillRequest,
    refillRequestDays,
    clinicSourceName,
    fromUpload,
    canManagePrescriptions,
    canManageCustomers,
    canCreateInvoice,
    portalUploadData,
    }) => {
    
    const inputRef = useRef(null);
    const durationRefs = useRef({});
    const doctorRef = useRef(null);
    const [showImagePanel, setShowImagePanel] = React.useState(false);

    React.useEffect(() => {
        if (!visible) setShowImagePanel(false);
    }, [visible]);

    useEffect(() => {
        if (visible) {
        setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [visible]);

    useEffect(() => {
    const handleEnterAsTab = (e) => {
        if (!visible) return;
        if (e.key !== "Enter") return;

        const target = e.target;
        const tag = target.tagName;
        const isInput = ["INPUT", "TEXTAREA", "SELECT"].includes(tag);
        const isDisabled = target.disabled || target.readOnly || target.type === "hidden";

        if (!isInput || isDisabled) return;
        e.preventDefault();

        if (target.placeholder === "Quantity") {
        addNewItem();
        return;
        }

        const form = target.form;
        if (!form) return;

        const focusable = Array.from(
        form.querySelectorAll(
            'input:not([disabled]):not([readonly]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), .p-dropdown, .p-calendar'
        )
        ).filter(el => el.offsetParent !== null); 

        const index = focusable.findIndex(el => el.contains(target) || el === target);
        if (index > -1 && index < focusable.length - 1) {
        const next = focusable[index + 1];
        const input = next.querySelector("input") || next.querySelector("button") || next;
        input.focus?.();
        }
    };

    document.addEventListener("keydown", handleEnterAsTab);
    return () => document.removeEventListener("keydown", handleEnterAsTab);
    }, [visible, prescription.items]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!visible) return;

            if (event.ctrlKey && (event.key === "a" || event.key === "A")) {
                event.preventDefault(); 
                if (prescription.items.length > 0) {
                    prodInputRefs.current[prescription.items[0].id]?.focus();
                }
            } else if (event.ctrlKey && (event.key === "s"||event.key === "S")) {
            event.preventDefault();
            addNewItem();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [visible, prescription.items.length]);

    // ── Shared header buttons ─────────────────────────────────────────────
    const headerButtons = (
        <div>
            <div className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ margin: '10px' }}>
                <div className="text-xl font-bold mb-2">{dialogName}</div>
                <div className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ margin: '10px' }}></div>
                <Button label="Revise" icon="pi pi-pencil" className="p-button-warning p-mr-2"
                    disabled={!isReadOnly || isRefillRequest || !canManagePrescriptions} onClick={handleRevise}
                    style={{borderRadius:"6px", fontSize:"14px"}} size="medium" />
                {canCreateInvoice && (
                <Button label="Convert" icon="pi pi-refresh" className="p-button-secondary"
                    disabled={!isReadOnly} onClick={handleConvertClick}
                    style={{borderRadius:"6px", fontSize:"14px"}} size="medium" />
                )}
                {canManagePrescriptions && (
                <Button label="Save & Convert" name="saveAndConvert" icon="pi pi-sync"
                    type="submit" form="prescriptionForm"
                    disabled={isReadOnly || isRevising} className="p-button-success"
                    style={{borderRadius:"6px", fontSize:"14px"}} size="medium" />
                )}
                {canManagePrescriptions && (
                // Dual-purpose: while creating/revising, submits the full
                // create/revise payload as before; while viewing an existing
                // prescription, the SAME click instead persists just
                // quantity/duration/the refill-reminder flag in place — see
                // handleSubmit's isReadOnly branch in Prescriptions.js. One
                // button, not two "Save"s where one is always disabled.
                <Button label="Save" name="save" icon="pi pi-save"
                    type="submit" form="prescriptionForm"
                    className="p-button-primary"
                    style={{borderRadius:"6px", fontSize:"14px"}} size="medium" />
                )}
                <Button label="Cancel" icon="pi pi-times" outlined onClick={cancelhandler}
                    className="p-button-secondary"
                    style={{borderRadius:"6px", fontSize:"14px"}} size="medium" />
                {portalUploadData?.file_url && (
                    <Button
                        label={showImagePanel ? "Hide Image" : "View Prescription Image"}
                        icon={showImagePanel ? "pi pi-times" : "pi pi-image"}
                        severity="info" outlined
                        onClick={(e) => { e.preventDefault(); setShowImagePanel(p => !p); }}
                        style={{borderRadius:"6px", fontSize:"14px"}} size="medium"
                    />
                )}
            </div>
            <div style={{marginTop:"10px",height:"1px",background:"linear-gradient(to right,rgb(200,200,200),rgb(200,200,200))",borderRadius:"2px",boxShadow:"0px 2px 4px rgba(0,0,0,0.1)"}}></div>
        </div>
    );

    // ── Shared form body ──────────────────────────────────────────────────
    const formBody = (
        <form id="prescriptionForm" onSubmit={handleSubmit}>
            {isRefillRequest && (
                <div style={{display:"flex",alignItems:"center",gap:12,background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:"10px 16px",marginBottom:16,flexWrap:"wrap"}}>
                    <i className={clinicSourceName ? "pi pi-hospital" : "pi pi-refresh"} style={{color:"#1d4ed8",fontSize:16,flexShrink:0}} />
                    <span style={{fontSize:13,color:"#1e40af",fontWeight:500}}>
                        {clinicSourceName ? `Prescription from ${clinicSourceName}` : "Refill Request"}
                    </span>
                    {refillRequestDays && (
                        <span style={{background:"#1d4ed8",color:"white",padding:"3px 10px",borderRadius:20,fontSize:12,fontWeight:600}}>
                            {refillRequestDays} days requested
                        </span>
                    )}
                    <span style={{fontSize:12.5,color:"#3b82f6"}}>
                        {clinicSourceName
                            ? "The doctor didn't set a fill quantity — set it for each item below before converting."
                            : "Review dosage & frequency below, then set the quantity before converting."}
                    </span>
                </div>
            )}
            <div className="formgrid grid">
                <div className="field col">
                    <label htmlFor="customer" className="font-bold">Customer:</label>
                    <div className="p-inputgroup">
                        <AutoComplete id="customer" ref={inputRef}
                            value={prescription.customer} suggestions={filteredCustomers}
                            completeMethod={(e) => handleSearchCustomer(e.query)} field="displayLabel"
                            onChange={(e) => setPrescription({ ...prescription, customer: e.value })}
                            disabled={isReadOnly || modulehandler==='INV'} required />
                        <span className={`p-inputgroup-addon ${isReadOnly || modulehandler==='INV' || !canManageCustomers ? 'disabled' : ''}`}
                            onClick={() => !isReadOnly && modulehandler!=='INV' && canManageCustomers && handleCustomerDialogVisible()}
                            style={{cursor: isReadOnly || modulehandler==='INV' || !canManageCustomers ? 'not-allowed' : 'pointer',
                                    opacity: isReadOnly || modulehandler==='INV' || !canManageCustomers ? 0.5 : 1}}>
                            <b>+</b>
                        </span>
                    </div>
                </div>
                <div className="field col">
                    <label className="font-bold">Duration (days):</label>
                    <InputNumber value={prescription.duration} onValueChange={(e) => handleDurationChange(e.value)} placeholder="Duration" />
                </div>
                {isReadOnly && (
                    <div className="field col" style={{display:"flex",flexDirection:"column",gap:6}}>
                        <label className="font-bold">Auto-create refill reminder:</label>
                        <InputSwitch
                            checked={prescription.auto_create_refill_reminder !== false}
                            onChange={(e) => setPrescription({ ...prescription, auto_create_refill_reminder: e.value })}
                        />
                    </div>
                )}
                <div className="field col">
                    <label htmlFor="date" className="font-bold">Date:</label>
                    <Calendar id="date" value={ymdToDate(toYmd(prescription.date))}
                        onChange={(e) => setPrescription({ ...prescription, date: e.value })}
                        dateFormat="yy-mm-dd" disabled={isReadOnly} showButtonBar
                        onFocus={() => { setTimeout(() => { const t = document.querySelector('.p-datepicker-today > span'); if(t) t.focus(); }, 150); }} />
                </div>
                <div className="field col">
                    <label htmlFor="doctor_name" className="font-bold">Doctor Name:</label>
                    <InputText id="doctor_name" ref={doctorRef} value={prescription.doctor_name}
                        onChange={(e) => setPrescription({ ...prescription, doctor_name: e.target.value })}
                        disabled={isReadOnly} />
                </div>
            </div>
            <div>
                <DataTable value={prescription.items} className="invoice-table" scrollable scrollHeight="400px" header={header}>
                    <Column header="Product" body={(rowData) =>
                        isReadOnly ? (
                            <div style={{display:"flex",alignItems:"center",gap:"8px"}}>
                                <span>{rowData.product?.name}</span>
                                {rowData.is_refill_item && <span style={{background:"#2e7d32",color:"white",padding:"2px 6px",fontSize:"11px",borderRadius:"4px"}}>Refill</span>}
                            </div>
                        ) : (
                            <div id={rowData.id}><ProductAutoCompleteCell rowData={rowData} onValueChange={handleItemChange} inputRefMap={prodInputRefs.current} /></div>
                        )} />
                    <Column header="Dosage" body={(rowData) =>
                        isReadOnly ? <span>{rowData.dosage||''}</span> :
                        <InputText value={rowData.dosage} onChange={(e) => handleItemChange(rowData.id,'dosage',e.target.value)} placeholder="Dosage" required />} />
                    <Column header="Frequency" body={(rowData) =>
                        isReadOnly ? <span>{rowData.frequency||''}</span> :
                        <Dropdown value={rowData.frequency} options={frequency}
                            onChange={(e) => { handleItemChange(rowData.id,'frequency',e.target.value); setTimeout(()=>durationRefs.current[rowData.id]?.focus?.(),10); }}
                            onKeyDown={(e) => { if(e.key==='Enter'){e.preventDefault();e.target.click();} }}
                            placeholder="Frequency" required />} />
                    <Column header="Duration" body={(rowData) =>
                        <InputNumber id={`duration-${rowData.id}`} value={rowData.duration}
                            ref={(el) => (durationRefs.current[rowData.id]=el)}
                            onValueChange={(e) => handleItemChange(rowData.id,'duration',e.value)}
                            placeholder="Set Duration" required={!isReadOnly} />} />
                    <Column
                        header="Quantity"
                        body={(rowData) => (
                            <InputNumber value={rowData.quantity} onValueChange={(e) => handleItemChange(rowData.id,'quantity',e.value)}
                                placeholder={isReadOnly ? "Set qty" : "Quantity"} min={isReadOnly ? 1 : undefined} required={!isReadOnly}
                                showButtons={isReadOnly} buttonLayout="horizontal"
                                decrementButtonClassName="p-button-secondary p-button-sm" incrementButtonClassName="p-button-secondary p-button-sm"
                                incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                inputStyle={isReadOnly ? {width:70,textAlign:"center"} : undefined} />
                        )} />
                    {!isReadOnly && (
                        <Column header="Actions" body={(rowData) => (
                            <Button type="button" icon="pi pi-trash" className="p-button-danger" onClick={() => removeItem(rowData.id)} />
                        )} />
                    )}
                </DataTable>
            </div>
        </form>
    );

    // ── Image panel (rendered inside the dialog, no z-index issues) ──────
    const imagePanelContent = portalUploadData?.file_url && showImagePanel && (
        <div style={{
            width: "30%", minWidth: 300, flexShrink: 0,
            background: "#1a1a2e", display: "flex", flexDirection: "column",
            borderRight: "1px solid #333",
        }}>
            <div style={{
                padding: "10px 14px", background: "#0f0f1a",
                borderBottom: "1px solid #333",
                display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
                <div style={{ color: "white", fontSize: 13, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {portalUploadData.original_name}
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <a href={portalUploadData.file_url} target="_blank" rel="noreferrer"
                        style={{ color: "#60a5fa", fontSize: 11, textDecoration: "none", display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 6, border: "1px solid #334" }}>
                        <i className="pi pi-external-link" style={{ fontSize: 11 }} /> Full
                    </a>
                    <button onClick={() => setShowImagePanel(false)}
                        style={{ background: "none", border: "1px solid #334", borderRadius: 6, color: "#888", cursor: "pointer", padding: "4px 8px", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                        <i className="pi pi-times" style={{ fontSize: 10 }} /> Hide
                    </button>
                </div>
            </div>
            <div style={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}>
                {portalUploadData.file_type === "pdf" ? (
                    <iframe src={portalUploadData.file_url} title="Prescription" style={{ width: "100%", height: "100%", border: "none" }} />
                ) : (
                    <img src={portalUploadData.file_url} alt="Uploaded prescription"
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                )}
            </div>
        </div>
    );

    // ── fromUpload: inline panel, no Dialog ──────────────────────────────
    if (fromUpload) {
        if (!visible) return null;
        return (
            <div style={{position:'absolute',inset:0,zIndex:10,background:'#fff',display:'flex',flexDirection:'column',overflow:'hidden'}}>
                <div style={{borderBottom:'1px solid #e5e7eb',flexShrink:0,padding:'4px 0'}}>
                    {headerButtons}
                </div>
                <div style={{flex:1,display:'flex',overflow:'hidden'}}>
                    {imagePanelContent}
                    <div style={{flex:1,overflow:'auto',padding:'16px'}}>
                        {formBody}
                    </div>
                </div>
            </div>
        );
    }

    // ── Normal: full-screen Dialog ────────────────────────────────────────
    return (
        <Dialog header={headerButtons} visible={visible} onHide={onHide}
            modal draggable={false} closable={false} maximized={true}
            breakpoints={{ '960px': '75vw', '641px': '90vw' }}>
            <div style={{ display: 'flex', height: '100%', overflow: 'hidden', margin: '-1rem' }}>
                {imagePanelContent}
                <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
                    {formBody}
                </div>
            </div>
        </Dialog>
    );
};

export default PrescriptionDialog;